import React from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader, ResponsiveTable, StatusBadge, CardSkeleton, EmptyState } from "@/admin/ui";

function InlineEdit({ value, onSave, prefix = "", testid }) {
  const [v, setV] = React.useState(value);
  const [editing, setE] = React.useState(false);
  React.useEffect(() => setV(value), [value]);
  if (!editing) return (
    <button onClick={() => setE(true)} className="text-white/85 hover:text-[#d4af37] tabular-nums" data-testid={testid}>
      {prefix}{typeof value === "number" ? value.toLocaleString() : value}
    </button>
  );
  return (
    <div className="flex gap-1">
      <input value={v} onChange={(e) => setV(e.target.value)} className="lp-input py-1 max-w-[100px] tabular-nums" data-testid={`${testid}-input`} data-autofocus />
      <button className="lp-btn-primary text-xs py-1 px-2" onClick={() => { onSave(v); setE(false); }} data-testid={`${testid}-save`}>Save</button>
      <button className="lp-btn-secondary text-xs py-1 px-2" onClick={() => { setV(value); setE(false); }}>×</button>
    </div>
  );
}

export default function AdminPlans() {
  const [plans, setPlans] = React.useState(null);
  const [products, setProducts] = React.useState({});
  const load = async () => {
    const [p, pr] = await Promise.all([api.get("/api/admin/plans"), api.get("/api/admin/products")]);
    setPlans(p.data.items);
    setProducts(Object.fromEntries(pr.data.items.map((x) => [x.id, x])));
  };
  React.useEffect(() => { load(); }, []);
  const save = async (plan, field, value) => {
    const numeric = ["price_monthly", "monthly_requests", "max_api_keys", "rate_limit_per_second"].includes(field);
    try {
      await api.put(`/api/admin/plans/${plan.id}`, { [field]: numeric ? Number(value) : value });
      toast.success("Plan updated"); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="space-y-6" data-testid="admin-plans-page">
      <PageHeader title="Plans & pricing (legacy)" description="Directly editable pricing/quotas. For immutable versioning use Plans V2."
        badge={<StatusBadge tone="neutral">Legacy</StatusBadge>} />
      {!plans ? <CardSkeleton h="h-40" /> : plans.length === 0 ? (
        <EmptyState title="No legacy plans" />
      ) : (
        <ResponsiveTable minWidth={800}>
          <thead className="lp-table-sticky"><tr>
            <th>Product</th><th>Tier</th><th className="text-right">Price</th><th className="text-right">Monthly req</th><th className="text-right">Rate/s</th><th className="text-right">Keys</th>
          </tr></thead>
          <tbody>
            {plans.map((p) => (
              <tr key={p.id} data-testid={`admin-plan-${p.id}`}>
                <td className="text-white/85">{products[p.product_id]?.name || "—"}</td>
                <td><StatusBadge tone="brand">{p.tier}</StatusBadge></td>
                <td className="text-right"><InlineEdit value={p.price_monthly} onSave={(v) => save(p, "price_monthly", v)} prefix="$" testid={`price-${p.id}`} /></td>
                <td className="text-right"><InlineEdit value={p.monthly_requests} onSave={(v) => save(p, "monthly_requests", v)} testid={`reqs-${p.id}`} /></td>
                <td className="text-right"><InlineEdit value={p.rate_limit_per_second} onSave={(v) => save(p, "rate_limit_per_second", v)} testid={`rate-${p.id}`} /></td>
                <td className="text-right"><InlineEdit value={p.max_api_keys} onSave={(v) => save(p, "max_api_keys", v)} testid={`keys-${p.id}`} /></td>
              </tr>
            ))}
          </tbody>
        </ResponsiveTable>
      )}
    </div>
  );
}
